import { Link } from 'react-router-dom'
import { Clock, Calendar, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import SmartImage from '@/components/SmartImage'
import type { BlogPostWithContent } from '../types/blog.types'

interface BlogCardProps {
  post: BlogPostWithContent
}

export function BlogCard({ post }: BlogCardProps) {
  const { t } = useTranslation('blog')
  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="block bg-garage-card rounded-xl shadow-garage hover:shadow-garage-hover transition-all duration-300 overflow-hidden group h-full border border-garage-border hover:border-garage-accent hover:-translate-y-1"
    >
      {/* Featured Image */}
      <div className="relative h-56 bg-gradient-to-br from-garage-primary to-garage-accent overflow-hidden">
        {post.featured_image_id ? (
          <>
            <SmartImage
              src={`/images/${post.featured_image_id}.jpg`}
              alt={(post as any).title || post.content?.title || 'Blog post'}
              settings={
                (post as any).image_settings?.[
                  `/images/${post.featured_image_id}.jpg`
                ]
              }
              className="w-full h-full transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-garage-cream/30">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Category Badge */}
        {post.category && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1.5 bg-garage-accent/90 backdrop-blur-sm text-garage-cream px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg border border-garage-cream/20">
              {post.category.translation.name}
            </span>
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-garage-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Card Content */}
      <div className="p-5 sm:p-6 flex flex-col flex-grow">
        {/* Title */}
        <h3 className="text-xl sm:text-2xl font-bold text-garage-dark mb-3 line-clamp-2 font-garage-title group-hover:text-garage-accent transition-colors duration-300 leading-tight">
          {(post as any).title || post.content?.title || 'Untitled'}
        </h3>

        {/* Excerpt */}
        {((post as any).excerpt || post.content?.excerpt) && (
          <p className="text-garage-text/80 mb-4 line-clamp-3 text-sm sm:text-base leading-relaxed">
            {(post as any).excerpt || post.content?.excerpt}
          </p>
        )}

        {/* Tags - Mobile Optimized */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1.5">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-garage-accent/10 text-garage-accent rounded-md text-xs font-semibold border border-garage-accent/20 hover:bg-garage-accent hover:text-garage-cream transition-all duration-200"
                >
                  <Tag className="h-2.5 w-2.5" />
                  {tag.translation.name}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 bg-garage-surface text-garage-text/60 rounded-md text-xs font-medium">
                  +{post.tags.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Metadata & Read More */}
        <div className="flex items-center justify-between pt-4 border-t border-garage-border/50 mt-auto">
          <div className="flex items-center gap-3 text-xs sm:text-sm text-garage-text/70">
            {post.read_time_minutes && (
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="font-medium">
                  {t('minRead', { count: post.read_time_minutes })}
                </span>
              </div>
            )}
            {post.published_at && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline font-medium">
                  {formatDate(post.published_at)}
                </span>
                <span className="sm:hidden font-medium">
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Read More Arrow */}
          <div className="flex items-center gap-1.5 text-garage-accent font-bold text-sm group-hover:gap-2.5 transition-all duration-300">
            <span className="hidden sm:inline">{t('readMore')}</span>
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  )
}
